import { useState, useEffect, useCallback, useRef } from 'react';
import { useMobileAuth } from '../../context/MobileAuthContext';
import MobileLayout from '../../components/mobile/MobileLayout';
import { ManageHeader } from '../../components/mobile/ManageKit';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import {
  Building2, Users, CreditCard, Link2, Clock, ScrollText, Bell, LifeBuoy,
  ChevronRight, ChevronLeft, Save,
  Loader2, Plus, Ticket, CheckCircle, AlertTriangle, Eye, EyeOff,
  RefreshCw, Send, UserPlus, Shield, Zap, SlidersHorizontal,
  FileText, ShoppingCart, Gauge,
} from 'lucide-react';
import { WheelTimePicker } from '../../components/mobile/WheelTimePicker';
import { DatePicker } from '../../components/ui/date-picker';
import { formatDistanceToNow } from 'date-fns';

/* ════════════════════════════════════════════════════════════
   Shared helpers
   ════════════════════════════════════════════════════════════ */
const LoadingSpinner = () => (
  <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[var(--m-text-muted)]" /></div>
);

const SubHeader = ({ title, onBack, children }) => (
  <div className="bg-[var(--m-bg-card)] px-4 py-3 sticky top-0 z-10 safe-top">
    <div className="flex items-center gap-3">
      <button onClick={onBack} className="p-1 -ml-1 text-[var(--m-text-secondary)] hover:text-[var(--m-text)]" data-testid="settings-sub-back-btn">
        <ChevronLeft className="h-5 w-5" />
      </button>
      <h1 className="flex-1 text-[var(--m-text)] font-bold text-base" data-testid="settings-sub-title">{title}</h1>
      {children}
    </div>
  </div>
);

const FieldGroup = ({ label, children }) => (
  <div className="space-y-1">
    <label className="text-xs text-[var(--m-text-secondary)]">{label}</label>
    {children}
  </div>
);

const TextInput = ({ value, onChange, placeholder, disabled, testId, type = 'text' }) => (
  <input type={type} value={value || ''} onChange={onChange} placeholder={placeholder} disabled={disabled}
    className={`w-full bg-[var(--m-bg)] border border-[var(--m-border-light)] rounded-lg px-3 py-2.5 text-sm text-[var(--m-text)] placeholder-[var(--m-text-muted)] ${disabled ? 'opacity-50' : ''}`}
    data-testid={testId} />
);

const SelectInput = ({ value, onChange, options, testId }) => (
  <select value={value || ''} onChange={onChange}
    className="w-full bg-[var(--m-bg)] border border-[var(--m-border-light)] rounded-lg px-3 py-2.5 text-sm text-[var(--m-text)]"
    data-testid={testId}>
    {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

const SectionCard = ({ title, desc, children }) => (
  <div className="rounded-lg border border-[var(--m-border)] bg-[var(--m-bg-card)] p-4 space-y-3">
    {title && <div className="text-sm font-medium text-[var(--m-text)]">{title}</div>}
    {desc && <div className="text-xs text-[var(--m-text-secondary)] -mt-1">{desc}</div>}
    {children}
  </div>
);

const RuleToggle = ({ label, desc, checked, onChange, testId }) => (
  <div className="flex items-start justify-between gap-3 p-3 rounded-lg border border-[var(--m-border)] bg-[var(--m-bg)]">
    <div className="flex-1 min-w-0">
      <div className="text-sm text-[var(--m-text)] font-medium">{label}</div>
      {desc && <div className="text-xs text-[var(--m-text-secondary)] mt-0.5">{desc}</div>}
    </div>
    <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
  </div>
);

/* ════════════════════════════════════════════════════════════
   1. Organisation
   ════════════════════════════════════════════════════════════ */
const OrganisationScreen = ({ api, operator, onBack }) => {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/api/tenants/${operator.client_id}`)
      .then((r) => setForm({
        company_name: r.data.company_name || '',
        legal_entity_name: r.data.legal_entity_name || '',
        business_number: r.data.business_number || '',
        industry: r.data.industry || 'earthmoving',
        country: r.data.country || 'AU',
        timezone: r.data.timezone || 'Australia/Sydney',
        date_format: r.data.date_format || 'DD/MM/YYYY',
        time_format: r.data.time_format || '24h',
        currency: r.data.currency || 'AUD',
        currency_symbol: r.data.currency_symbol || '$',
        tax_name: r.data.tax_name || 'GST',
        tax_rate: r.data.tax_rate ?? 10,
        default_working_hours: r.data.default_working_hours || { start: '07:00', end: '17:00' },
      }))
      .catch(() => toast.error('Failed to load organisation'))
      .finally(() => setLoading(false));
  }, [api, operator.client_id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/api/tenants/${operator.client_id}`, form);
      toast.success('Organisation updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  if (loading || !form) return <MobileLayout><SubHeader title="Organisation" onBack={onBack} /><LoadingSpinner /></MobileLayout>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const INDUSTRIES = [
    { value: 'crane_hire', label: 'Crane Hire' },
    { value: 'earthmoving', label: 'Earthmoving' },
    { value: 'haulage', label: 'Haulage' },
    { value: 'concrete_pumping', label: 'Concrete Pumping' },
    { value: 'street_sweeping', label: 'Street Sweeping' },
  ];

  return (
    <MobileLayout>
      <SubHeader title="Organisation" onBack={onBack}>
        <Button size="sm" className="btn-volt h-7 text-xs" onClick={handleSave} disabled={saving} data-testid="org-save-btn">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Save className="h-3.5 w-3.5 mr-1" /> Save</>}
        </Button>
      </SubHeader>
      <div className="p-4 space-y-4" data-testid="org-content">
        <SectionCard title="Company Information">
          <FieldGroup label="Client ID">
            <TextInput value={operator.client_id} disabled testId="org-client-id" />
          </FieldGroup>
          <FieldGroup label="Company Name">
            <TextInput value={form.company_name} onChange={(e) => set('company_name', e.target.value)} placeholder="Your Company Name" testId="org-company-name" />
          </FieldGroup>
          <FieldGroup label="Legal Entity Name">
            <TextInput value={form.legal_entity_name} onChange={(e) => set('legal_entity_name', e.target.value)} placeholder="Registered legal name" testId="org-legal-name" />
          </FieldGroup>
          <FieldGroup label="ABN / Business Number">
            <TextInput value={form.business_number} onChange={(e) => set('business_number', e.target.value)} placeholder="e.g. 12 345 678 901" testId="org-abn" />
          </FieldGroup>
          <FieldGroup label="Industry">
            <SelectInput value={form.industry} onChange={(e) => set('industry', e.target.value)} options={INDUSTRIES} testId="org-industry" />
          </FieldGroup>
        </SectionCard>

        <SectionCard title="Tax & Currency">
          <div className="grid grid-cols-2 gap-3">
            <FieldGroup label="Tax Name">
              <TextInput value={form.tax_name} onChange={(e) => set('tax_name', e.target.value)} placeholder="GST" testId="org-tax-name" />
            </FieldGroup>
            <FieldGroup label="Tax Rate (%)">
              <TextInput value={form.tax_rate} onChange={(e) => set('tax_rate', parseFloat(e.target.value) || 0)} type="number" testId="org-tax-rate" />
            </FieldGroup>
            <FieldGroup label="Currency Symbol">
              <TextInput value={form.currency_symbol} onChange={(e) => set('currency_symbol', e.target.value)} testId="org-currency-symbol" />
            </FieldGroup>
            <FieldGroup label="Currency Code">
              <TextInput value={form.currency} onChange={(e) => set('currency', e.target.value)} testId="org-currency" />
            </FieldGroup>
          </div>
        </SectionCard>

        <SectionCard title="Regional">
          <FieldGroup label="Timezone">
            <TextInput value={form.timezone} onChange={(e) => set('timezone', e.target.value)} testId="org-timezone" />
          </FieldGroup>
          <div className="grid grid-cols-2 gap-3">
            <FieldGroup label="Date Format">
              <SelectInput value={form.date_format} onChange={(e) => set('date_format', e.target.value)} testId="org-date-format"
                options={[{ value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' }, { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' }, { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' }]} />
            </FieldGroup>
            <FieldGroup label="Time Format">
              <SelectInput value={form.time_format} onChange={(e) => set('time_format', e.target.value)} testId="org-time-format"
                options={[{ value: '24h', label: '24-hour' }, { value: '12h', label: '12-hour' }]} />
            </FieldGroup>
          </div>
        </SectionCard>

        <SectionCard title="Working Hours">
          <div className="grid grid-cols-2 gap-3">
            <FieldGroup label="Start">
              <WheelTimePicker value={form.default_working_hours?.start || '07:00'} testId="org-start-time" minuteStep={5}
                onChange={(v) => set('default_working_hours', { ...form.default_working_hours, start: v })} className="w-full bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)]" />
            </FieldGroup>
            <FieldGroup label="End">
              <WheelTimePicker value={form.default_working_hours?.end || '17:00'} testId="org-end-time" minuteStep={5}
                onChange={(v) => set('default_working_hours', { ...form.default_working_hours, end: v })} className="w-full bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)]" />
            </FieldGroup>
          </div>
        </SectionCard>
      </div>
    </MobileLayout>
  );
};

/* ════════════════════════════════════════════════════════════
   2. Users
   ════════════════════════════════════════════════════════════ */
const UsersScreen = ({ api, onBack }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ full_name: '', email: '', roles: ['dispatcher'] });
  const [addSaving, setAddSaving] = useState(false);
  const [tempPwd, setTempPwd] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get('/api/users'); setUsers(r.data || []); }
    catch { /* no-op */ } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const filtered = users.filter((u) =>
    (u.full_name || u.username || u.email || '').toLowerCase().includes(q.toLowerCase())
  );

  const toggleStatus = async (u) => {
    try {
      await api.put(`/api/users/${u.id}/status`, { is_active: u.is_active === false });
      toast.success(u.is_active === false ? 'User activated' : 'User deactivated');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const resetPassword = async (u) => {
    try {
      const r = await api.post(`/api/users/${u.id}/reset-password`);
      setTempPwd(r.data.temporary_password || r.data.password);
      toast.success('Password reset');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const addUser = async () => {
    if (!addForm.email || !addForm.full_name) return;
    setAddSaving(true);
    try {
      const r = await api.post('/api/users', addForm);
      toast.success('User created');
      setTempPwd(r.data.temporary_password || r.data.password || null);
      setShowAdd(false);
      setAddForm({ full_name: '', email: '', roles: ['dispatcher'] });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create user'); }
    finally { setAddSaving(false); }
  };

  const roleColor = (roles) => {
    if (roles?.includes('admin')) return 'bg-red-500/20 text-red-300';
    if (roles?.includes('dispatcher')) return 'bg-blue-500/20 text-blue-300';
    return 'bg-gray-500/20 text-[var(--m-text-body)]';
  };

  // Add user form
  if (showAdd) {
    return (
      <MobileLayout>
        <SubHeader title="Add User" onBack={() => setShowAdd(false)} />
        <div className="p-4 space-y-4" data-testid="users-add-form">
          <FieldGroup label="Full Name">
            <TextInput value={addForm.full_name} onChange={(e) => setAddForm((f) => ({ ...f, full_name: e.target.value }))}
              placeholder="John Smith" testId="users-add-name" />
          </FieldGroup>
          <FieldGroup label="Email">
            <TextInput value={addForm.email} onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="john@company.com" type="email" testId="users-add-email" />
          </FieldGroup>
          <FieldGroup label="Role">
            <SelectInput value={addForm.roles[0]} onChange={(e) => setAddForm((f) => ({ ...f, roles: [e.target.value] }))} testId="users-add-role"
              options={[{ value: 'admin', label: 'Admin' }, { value: 'dispatcher', label: 'Dispatcher' }]} />
          </FieldGroup>
          <Button className="w-full btn-volt" onClick={addUser} disabled={addSaving || !addForm.email || !addForm.full_name} data-testid="users-add-submit">
            {addSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserPlus className="h-4 w-4 mr-2" /> Create User</>}
          </Button>
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout>
      <SubHeader title="Users" onBack={onBack}>
        <Button size="sm" className="btn-volt h-7 text-xs" onClick={() => setShowAdd(true)} data-testid="users-add-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> Add
        </Button>
      </SubHeader>

      {/* Temp password modal */}
      {tempPwd && (
        <div className="mx-4 mt-3 p-3 rounded-lg border border-[#ffd108]/30 bg-[#ffd108]/10" data-testid="users-temp-pwd">
          <div className="text-xs text-[var(--m-text-secondary)] mb-1">Temporary Password</div>
          <div className="font-mono text-sm text-[var(--m-text)] bg-[var(--m-bg)] rounded px-3 py-2 select-all">{tempPwd}</div>
          <Button size="sm" variant="outline" className="mt-2 text-xs border-[var(--m-border-light)] text-[var(--m-text-body)]" onClick={() => setTempPwd(null)}>Dismiss</Button>
        </div>
      )}

      <div className="px-4 pt-3 pb-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users..."
          className="w-full bg-[var(--m-bg)] border border-[var(--m-border-light)] rounded-lg px-3 py-2 text-sm text-[var(--m-text)] placeholder-[var(--m-text-muted)]" />
      </div>

      <div className="px-4 pb-4 space-y-1.5" data-testid="users-list">
        {loading ? <LoadingSpinner /> : filtered.length === 0 ? (
          <div className="text-center py-12 text-[var(--m-text-muted)] text-sm">No users found</div>
        ) : (
          filtered.map((u) => (
            <div key={u.id} className="rounded-lg bg-[var(--m-bg-card)] p-3 space-y-1.5" data-testid={`user-${u.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[var(--m-text)] font-medium truncate">{u.full_name || u.username}</div>
                  <div className="text-xs text-[var(--m-text-secondary)] truncate">{u.email}</div>
                </div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${roleColor(u.roles)}`}>
                  {u.roles?.[0] || 'user'}
                </span>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <span className={`text-[10px] ${u.is_active === false ? 'text-red-400' : 'text-green-400'}`}>
                  {u.is_active === false ? 'Inactive' : 'Active'}
                </span>
                <div className="flex-1" />
                <button onClick={() => toggleStatus(u)} className="text-[10px] text-[var(--m-text-secondary)] hover:text-[var(--m-text)] px-2 py-1">
                  {u.is_active === false ? 'Activate' : 'Deactivate'}
                </button>
                <button onClick={() => resetPassword(u)} className="text-[10px] text-[var(--m-text-secondary)] hover:text-[#ffd108] px-2 py-1">
                  Reset Pwd
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </MobileLayout>
  );
};

/* ════════════════════════════════════════════════════════════
   3. Billing (native)
   ════════════════════════════════════════════════════════════ */
const BillingScreen = ({ api, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/billing/usage')
      .then((r) => setData(r.data))
      .catch(() => toast.error('Failed to load billing info'))
      .finally(() => setLoading(false));
  }, [api]);

  if (loading) return <MobileLayout><SubHeader title="Billing" onBack={onBack} /><LoadingSpinner /></MobileLayout>;
  if (!data) return <MobileLayout><SubHeader title="Billing" onBack={onBack} /><div className="p-6 text-center text-[var(--m-text-secondary)] text-sm" data-testid="billing-error">Unable to load billing information.</div></MobileLayout>;

  const pct = (used, limit) => limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;

  return (
    <MobileLayout>
      <SubHeader title="Billing" onBack={onBack} />
      <div className="p-4 space-y-4" data-testid="billing-content">
        <div className="rounded-lg border border-[var(--m-border)] bg-[var(--m-bg-card)] p-4 space-y-3" data-testid="billing-plan-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-[var(--m-text-secondary)] uppercase tracking-wide">Current Plan</div>
              <div className="text-lg font-bold text-[var(--m-text)]" data-testid="billing-tier-name">{data.tier_name || 'Free'}</div>
            </div>
            <div className="text-right">
              <div className="text-xl font-bold text-[#ffd108]" data-testid="billing-price">${data.base_price != null ? Number(data.base_price).toFixed(0) : '—'}</div>
              <div className="text-[10px] text-[var(--m-text-secondary)]">per month</div>
            </div>
          </div>
          {data.tier_description && <p className="text-xs text-[var(--m-text-secondary)]">{data.tier_description}</p>}
        </div>
        <UsageBar label="Operators" used={data.operators_full_used ?? data.operators_used ?? 0} limit={data.operators_total_limit ?? 0} pct={pct(data.operators_full_used ?? data.operators_used ?? 0, data.operators_total_limit ?? 0)} testId="billing-operators" />
        <UsageBar label="Office Users" used={data.users_used ?? 0} limit={data.users_total_limit ?? 0} pct={pct(data.users_used ?? 0, data.users_total_limit ?? 0)} testId="billing-users" />
        {data.can_upgrade && data.next_tier && (
          <div className="rounded-lg border border-[#ffd108]/30 bg-[#ffd108]/5 p-3" data-testid="billing-upgrade-hint">
            <p className="text-xs text-[var(--m-text-body)]">Need more? Upgrade to <span className="text-[#ffd108] font-semibold">{data.next_tier_config?.name || data.next_tier}</span>.</p>
          </div>
        )}
      </div>
    </MobileLayout>
  );
};

const UsageBar = ({ label, used, limit, pct, testId }) => (
  <div className="rounded-lg border border-[var(--m-border)] bg-[var(--m-bg-card)] p-3 space-y-2" data-testid={testId}>
    <div className="flex items-center justify-between">
      <span className="text-xs text-[var(--m-text-secondary)]">{label}</span>
      <span className="text-sm text-[var(--m-text)] font-medium">{used} / {limit}</span>
    </div>
    <div className="h-1.5 bg-[var(--m-bg)] rounded-full overflow-hidden">
      <div className={`h-full rounded-full transition-all ${pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-yellow-500' : 'bg-[#ffd108]'}`} style={{ width: `${pct}%` }} />
    </div>
  </div>
);

/* ════════════════════════════════════════════════════════════
   4. Accounting
   ════════════════════════════════════════════════════════════ */
const AccountingScreen = ({ api, onBack }) => {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null); // 'xero' | 'myob' | null

  useEffect(() => {
    api.get('/api/accounting/connections')
      .then((r) => setConnections(r.data?.connections || r.data || []))
      .catch(() => {/* no connections */})
      .finally(() => setLoading(false));
  }, [api]);

  const handleConnect = async (provider) => {
    try {
      setConnecting(provider);
      const endpoint = provider === 'xero' ? '/api/xero/connect' : '/api/myob/connect';
      const response = await api.get(endpoint);
      if (response.data?.auth_url) {
        window.location.href = response.data.auth_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to initiate ${provider} connection`);
      setConnecting(null);
    }
  };

  const providerLabel = (p) => ({ xero: 'Xero', myob: 'MYOB', quickbooks: 'QuickBooks' }[p] || p);
  const providerColor = (p) => ({ xero: 'bg-cyan-500/20 text-cyan-300', myob: 'bg-purple-500/20 text-purple-300', quickbooks: 'bg-green-500/20 text-green-300' }[p] || 'bg-gray-500/20 text-[var(--m-text-body)]');

  return (
    <MobileLayout>
      <SubHeader title="Accounting" onBack={onBack} />
      <div className="p-4 space-y-4" data-testid="accounting-content">
        {loading ? <LoadingSpinner /> : (
          <>
            {connections.length === 0 ? (
              <div className="text-center py-8" data-testid="accounting-empty">
                <Link2 className="h-10 w-10 mx-auto mb-3 text-gray-600" />
                <p className="text-sm text-[var(--m-text-secondary)] mb-1">No Accounting Connections</p>
                <p className="text-xs text-[var(--m-text-muted)] mb-4">Connect your accounting package to sync invoices and contacts.</p>
              </div>
            ) : (
              <>
                <p className="text-xs text-[var(--m-text-secondary)]">{connections.length} connection{connections.length !== 1 ? 's' : ''}</p>
                {connections.map((c) => (
                  <div key={c.id} className="rounded-lg border border-[var(--m-border)] bg-[var(--m-bg-card)] p-3 space-y-2" data-testid={`acct-conn-${c.id}`}>
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full uppercase font-mono ${providerColor(c.provider)}`}>{providerLabel(c.provider)}</span>
                      {c.is_default && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#ffd108]/20 text-[#ffd108]">Default</span>}
                      {c.status === 'needs_reauth' && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-300">Needs Reconnect</span>}
                    </div>
                    <div className="text-sm text-[var(--m-text)] font-medium">{c.label}</div>
                    {c.company_name && c.company_name !== c.label && <div className="text-xs text-[var(--m-text-secondary)]">{c.company_name}</div>}
                  </div>
                ))}
              </>
            )}

            {/* Connect buttons — always visible so admins can add more connections */}
            <div className="space-y-2 pt-2">
              <p className="text-xs text-[var(--m-text-muted)] font-medium uppercase tracking-wider">Connect a package</p>
              <button
                onClick={() => handleConnect('xero')}
                disabled={connecting !== null}
                className="w-full flex items-center gap-3 rounded-lg border border-[var(--m-border)] bg-[var(--m-bg-card)] p-3 active:opacity-70 disabled:opacity-50"
                data-testid="connect-xero-btn"
              >
                <span className="text-[10px] px-2 py-0.5 rounded-full uppercase font-mono bg-cyan-500/20 text-cyan-300">Xero</span>
                <span className="text-sm text-[var(--m-text)]">{connecting === 'xero' ? 'Redirecting...' : 'Connect Xero'}</span>
              </button>
              <button
                onClick={() => handleConnect('myob')}
                disabled={connecting !== null}
                className="w-full flex items-center gap-3 rounded-lg border border-[var(--m-border)] bg-[var(--m-bg-card)] p-3 active:opacity-70 disabled:opacity-50"
                data-testid="connect-myob-btn"
              >
                <span className="text-[10px] px-2 py-0.5 rounded-full uppercase font-mono bg-purple-500/20 text-purple-300">MYOB</span>
                <span className="text-sm text-[var(--m-text)]">{connecting === 'myob' ? 'Redirecting...' : 'Connect MYOB'}</span>
              </button>
            </div>
          </>
        )}
      </div>
    </MobileLayout>
  );
};

/* ════════════════════════════════════════════════════════════
   5. Timesheets
   ════════════════════════════════════════════════════════════ */
const TimesheetsScreen = ({ api, operator, onBack }) => {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/api/tenants/${operator.client_id}`)
      .then((r) => setForm({
        pay_cycle_type: r.data.pay_cycle_type || 'weekly',
        pay_cycle_day: r.data.pay_cycle_day ?? 1,
        pay_cycle_start_date: r.data.pay_cycle_start_date || '',
        payroll_calc_method: r.data.payroll_calc_method || 'actual_hours',
        overtime_method: r.data.overtime_method || 'disabled',
        overtime_threshold_hours: r.data.overtime_threshold_hours ?? 8,
      }))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [api, operator.client_id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/api/tenants/${operator.client_id}`, form);
      toast.success('Timesheet settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading || !form) return <MobileLayout><SubHeader title="Timesheets" onBack={onBack} /><LoadingSpinner /></MobileLayout>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const DAYS = [{ value: '1', label: 'Monday' }, { value: '2', label: 'Tuesday' }, { value: '3', label: 'Wednesday' }, { value: '4', label: 'Thursday' }, { value: '5', label: 'Friday' }, { value: '6', label: 'Saturday' }, { value: '7', label: 'Sunday' }];

  return (
    <MobileLayout>
      <SubHeader title="Timesheets" onBack={onBack}>
        <Button size="sm" className="btn-volt h-7 text-xs" onClick={handleSave} disabled={saving} data-testid="ts-save-btn">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Save className="h-3.5 w-3.5 mr-1" /> Save</>}
        </Button>
      </SubHeader>
      <div className="p-4 space-y-4" data-testid="timesheets-content">
        <SectionCard title="Pay Cycle">
          <FieldGroup label="Frequency">
            <SelectInput value={form.pay_cycle_type} onChange={(e) => set('pay_cycle_type', e.target.value)} testId="ts-frequency"
              options={[{ value: 'weekly', label: 'Weekly' }, { value: 'fortnightly', label: 'Fortnightly' }, { value: 'monthly', label: 'Monthly' }]} />
          </FieldGroup>
          {(form.pay_cycle_type === 'weekly' || form.pay_cycle_type === 'fortnightly') && (
            <FieldGroup label="Start Day">
              <SelectInput value={String(form.pay_cycle_day)} onChange={(e) => set('pay_cycle_day', parseInt(e.target.value))} options={DAYS} testId="ts-start-day" />
            </FieldGroup>
          )}
          {form.pay_cycle_type === 'monthly' && (
            <FieldGroup label="Day of Month">
              <SelectInput value={String(form.pay_cycle_day)} testId="ts-month-day"
                onChange={(e) => set('pay_cycle_day', parseInt(e.target.value))}
                options={Array.from({ length: 28 }, (_, i) => ({ value: String(i + 1), label: `${i + 1}${i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'}` }))} />
            </FieldGroup>
          )}
          {form.pay_cycle_type === 'fortnightly' && (
            <FieldGroup label="Reference Start Date">
              <DatePicker value={form.pay_cycle_start_date} onChange={(d) => set('pay_cycle_start_date', d)}
                className="w-full bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)]" testId="ts-ref-date" />
            </FieldGroup>
          )}
        </SectionCard>

        <SectionCard title="Calculation">
          <FieldGroup label="Payroll Method">
            <SelectInput value={form.payroll_calc_method} onChange={(e) => set('payroll_calc_method', e.target.value)} testId="ts-calc-method"
              options={[{ value: 'actual_hours', label: 'Actual Hours' }, { value: 'scheduled_hours', label: 'Scheduled Hours' }]} />
          </FieldGroup>
          <FieldGroup label="Overtime">
            <SelectInput value={form.overtime_method} onChange={(e) => set('overtime_method', e.target.value)} testId="ts-overtime"
              options={[{ value: 'disabled', label: 'Disabled' }, { value: 'daily', label: 'Daily' }, { value: 'weekly', label: 'Weekly' }]} />
          </FieldGroup>
          {form.overtime_method !== 'disabled' && (
            <FieldGroup label="Overtime Threshold (hrs)">
              <TextInput type="number" value={form.overtime_threshold_hours} onChange={(e) => set('overtime_threshold_hours', parseFloat(e.target.value) || 8)} testId="ts-ot-threshold" />
            </FieldGroup>
          )}
        </SectionCard>
      </div>
    </MobileLayout>
  );
};

/* ════════════════════════════════════════════════════════════
   6. Rules
   ════════════════════════════════════════════════════════════ */
const RulesScreen = ({ api, operator, onBack }) => {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/api/tenants/${operator.client_id}`)
      .then((r) => setForm({
        auto_confirm_jobs: r.data.auto_confirm_jobs ?? true,
        relaxed_allocation: r.data.relaxed_allocation ?? false,
        skip_docket_approval: r.data.skip_docket_approval ?? false,
        minimum_charge_enabled: r.data.minimum_charge_enabled ?? false,
        fuel_levy_enabled: r.data.fuel_levy_enabled ?? false,
        include_photos_in_docket_pdf: r.data.include_photos_in_docket_pdf ?? false,
        auto_email_docket_on_submit: r.data.auto_email_docket_on_submit ?? false,
        field_rules: r.data.field_rules || {},
      }))
      .catch(() => toast.error('Failed to load rules'))
      .finally(() => setLoading(false));
  }, [api, operator.client_id]);

  const toggle = async (key, value) => {
    const updated = { ...form, [key]: value };
    setForm(updated);
    try {
      await api.put(`/api/tenants/${operator.client_id}`, { [key]: value });
      toast.success('Rule updated');
    } catch {
      setForm((f) => ({ ...f, [key]: !value }));
      toast.error('Failed to update');
    }
  };

  const toggleFieldRule = async (ruleKey, subKey, value) => {
    const updatedRules = { ...form.field_rules, [ruleKey]: { ...form.field_rules[ruleKey], [subKey]: value } };
    setForm((f) => ({ ...f, field_rules: updatedRules }));
    try {
      await api.put(`/api/tenants/${operator.client_id}`, { field_rules: updatedRules });
      toast.success('Rule updated');
    } catch {
      toast.error('Failed to update');
    }
  };

  if (loading || !form) return <MobileLayout><SubHeader title="Rules" onBack={onBack} /><LoadingSpinner /></MobileLayout>;

  return (
    <MobileLayout>
      <SubHeader title="Rules" onBack={onBack} />
      <div className="p-4 space-y-4" data-testid="rules-content">
        <SectionCard title="Automation Rules">
          <RuleToggle label="Auto-Confirm Jobs" desc="Automatically set jobs to Confirmed when asset and operator are allocated"
            checked={form.auto_confirm_jobs} onChange={(v) => toggle('auto_confirm_jobs', v)} testId="rules-auto-confirm" />
          <RuleToggle label="Relaxed Allocation" desc="Skip drag-and-drop capability checks on the planning board"
            checked={form.relaxed_allocation} onChange={(v) => toggle('relaxed_allocation', v)} testId="rules-relaxed" />
          <RuleToggle label="Skip Docket Approval" desc="Dockets go straight to Approved after operator submission"
            checked={form.skip_docket_approval} onChange={(v) => toggle('skip_docket_approval', v)} testId="rules-skip-approval" />
        </SectionCard>

        <SectionCard title="Invoicing Rules">
          <RuleToggle label="Minimum Charge" desc="Enforce minimum charge amounts on invoices"
            checked={form.minimum_charge_enabled} onChange={(v) => toggle('minimum_charge_enabled', v)} testId="rules-min-charge" />
          <RuleToggle label="Fuel Levy" desc="Add fuel levy surcharge to invoices"
            checked={form.fuel_levy_enabled} onChange={(v) => toggle('fuel_levy_enabled', v)} testId="rules-fuel-levy" />
        </SectionCard>

        <SectionCard title="Docket Rules">
          <RuleToggle label="Include Photos in PDF" desc="Attach operator photos in the generated docket PDF"
            checked={form.include_photos_in_docket_pdf} onChange={(v) => toggle('include_photos_in_docket_pdf', v)} testId="rules-photos" />
          <RuleToggle label="Auto-Email Docket" desc="Automatically email docket PDF to customer on submit"
            checked={form.auto_email_docket_on_submit} onChange={(v) => toggle('auto_email_docket_on_submit', v)} testId="rules-auto-email" />
        </SectionCard>

        <SectionCard title="Field Rules">
          <RuleToggle label="Depot Time" desc="Track when operators leave the depot"
            checked={form.field_rules?.depot_time?.enabled || false}
            onChange={(v) => toggleFieldRule('depot_time', 'enabled', v)} testId="rules-depot-time" />
          <RuleToggle label="SMU Tracking" desc="Service Meter Unit tracking on equipment"
            checked={form.field_rules?.smu?.enabled || false}
            onChange={(v) => toggleFieldRule('smu', 'enabled', v)} testId="rules-smu" />
          <RuleToggle label="Additional Workers" desc="Allow adding extra workers to jobs"
            checked={form.field_rules?.additional_workers?.enabled ?? true}
            onChange={(v) => toggleFieldRule('additional_workers', 'enabled', v)} testId="rules-addl-workers" />
        </SectionCard>
      </div>
    </MobileLayout>
  );
};

/* ════════════════════════════════════════════════════════════
   7. Notifications
   ════════════════════════════════════════════════════════════ */
const NotificationsScreen = ({ api, onBack }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/notification-templates')
      .then((r) => setTemplates(r.data?.templates || []))
      .catch(() => {/* no-op */})
      .finally(() => setLoading(false));
  }, [api]);

  const toggleTemplate = async (t) => {
    const updated = !t.is_active;
    setTemplates((prev) => prev.map((x) => x.id === t.id ? { ...x, is_active: updated } : x));
    try {
      await api.put(`/api/notification-templates/${t.id}`, { is_active: updated });
      toast.success(updated ? 'Notification enabled' : 'Notification disabled');
    } catch {
      setTemplates((prev) => prev.map((x) => x.id === t.id ? { ...x, is_active: !updated } : x));
      toast.error('Failed to update');
    }
  };

  const eventLabel = (e) => (e || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const channelColor = (c) => ({ email: 'bg-blue-500/20 text-blue-300', sms: 'bg-green-500/20 text-green-300', push: 'bg-purple-500/20 text-purple-300' }[c] || 'bg-gray-500/20 text-[var(--m-text-body)]');

  return (
    <MobileLayout>
      <SubHeader title="Notifications" onBack={onBack} />
      <div className="p-4 space-y-3" data-testid="notifications-content">
        {loading ? <LoadingSpinner /> : templates.length === 0 ? (
          <div className="text-center py-12 text-[var(--m-text-muted)] text-sm" data-testid="notifications-empty">
            <Bell className="h-10 w-10 mx-auto mb-3 text-gray-600" />
            <p>No notification templates configured.</p>
            <p className="text-xs text-[var(--m-text-muted)] mt-1">Set up notifications in Settings.</p>
          </div>
        ) : (
          templates.map((t) => (
            <div key={t.id} className="flex items-start gap-3 p-3 rounded-lg bg-[var(--m-bg-card)] border border-[var(--m-border)]" data-testid={`notif-${t.id}`}>
              <div className="flex-1 min-w-0 space-y-1">
                <div className="text-sm text-[var(--m-text)] font-medium">{t.name || eventLabel(t.event_type)}</div>
                {t.event_type && <div className="text-[10px] text-[var(--m-text-muted)]">{eventLabel(t.event_type)}</div>}
                <div className="flex flex-wrap gap-1">
                  {(t.channels || [t.channel]).filter(Boolean).map((ch) => (
                    <span key={ch} className={`text-[10px] px-1.5 py-0.5 rounded-full ${channelColor(ch)}`}>{ch}</span>
                  ))}
                </div>
              </div>
              <Switch checked={t.is_active !== false} onCheckedChange={() => toggleTemplate(t)} data-testid={`notif-toggle-${t.id}`} />
            </div>
          ))
        )}
      </div>
    </MobileLayout>
  );
};

/* ════════════════════════════════════════════════════════════
   8. Support (native)
   ════════════════════════════════════════════════════════════ */
const SupportScreen = ({ api, onBack }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('general');
  const [submitting, setSubmitting] = useState(false);

  const fetchTickets = async (silent) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try { const res = await api.get('/api/support/tickets'); setTickets(res.data || []); }
    catch { /* no-op */ } finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { fetchTickets(false); }, [api]); // eslint-disable-line

  const submitTicket = async () => {
    if (!subject.trim() || !description.trim()) return;
    setSubmitting(true);
    try {
      await api.post('/api/support/tickets', { subject, description, category });
      toast.success('Ticket created');
      setSubject(''); setDescription(''); setCategory('general'); setShowNew(false);
      fetchTickets(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const statusColor = (s) => {
    switch (s) {
      case 'new': return 'bg-purple-500/30 text-purple-300';
      case 'open': case 'in_progress': return 'bg-blue-500/30 text-blue-300';
      case 'waiting_on_customer': return 'bg-orange-500/30 text-orange-300';
      case 'resolved': case 'closed': return 'bg-green-500/30 text-green-300';
      default: return 'bg-gray-500/30 text-[var(--m-text-body)]';
    }
  };
  const statusIcon = (s) => {
    if (['resolved', 'closed'].includes(s)) return <CheckCircle className="h-4 w-4 text-green-400" />;
    if (s === 'waiting_on_customer') return <AlertTriangle className="h-4 w-4 text-orange-400" />;
    return <Clock className="h-4 w-4 text-blue-400" />;
  };

  const CATEGORIES = [
    { value: 'general', label: 'General' }, { value: 'technical', label: 'Technical Issue' },
    { value: 'billing', label: 'Billing' }, { value: 'feature_request', label: 'Feature Request' }, { value: 'bug_report', label: 'Bug Report' },
  ];

  if (showNew) {
    return (
      <MobileLayout>
        <SubHeader title="New Ticket" onBack={() => setShowNew(false)} />
        <div className="p-4 space-y-4" data-testid="support-new-ticket-form">
          <FieldGroup label="Subject">
            <TextInput value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Brief summary" testId="support-ticket-subject" />
          </FieldGroup>
          <FieldGroup label="Category">
            <SelectInput value={category} onChange={(e) => setCategory(e.target.value)} options={CATEGORIES} testId="support-ticket-category" />
          </FieldGroup>
          <FieldGroup label="Description">
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={5}
              className="w-full bg-[var(--m-bg)] border border-[var(--m-border-light)] rounded-lg px-3 py-2.5 text-sm text-[var(--m-text)] placeholder-[var(--m-text-muted)] resize-none"
              placeholder="Describe your issue..." data-testid="support-ticket-description" />
          </FieldGroup>
          <Button className="w-full btn-volt" disabled={!subject.trim() || !description.trim() || submitting} onClick={submitTicket} data-testid="support-ticket-submit">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" /> Submit Ticket</>}
          </Button>
        </div>
      </MobileLayout>
    );
  }

  const openCount = tickets.filter((t) => !['resolved', 'closed'].includes(t.status)).length;

  return (
    <MobileLayout>
      <SubHeader title="Support" onBack={onBack}>
        <button onClick={() => fetchTickets(true)} disabled={refreshing} className="p-1 text-[var(--m-text-secondary)] hover:text-[var(--m-text)]" data-testid="support-refresh-btn">
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
        <Button size="sm" className="btn-volt h-7 text-xs" onClick={() => setShowNew(true)} data-testid="support-new-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> New
        </Button>
      </SubHeader>
      <div className="p-4 space-y-3" data-testid="support-tickets-list">
        {openCount > 0 && <p className="text-xs text-[var(--m-text-secondary)]">{openCount} open ticket{openCount !== 1 ? 's' : ''}</p>}
        {loading ? <LoadingSpinner /> : tickets.length === 0 ? (
          <div className="text-center py-12" data-testid="support-empty-state">
            <Ticket className="h-10 w-10 mx-auto mb-3 text-gray-600" />
            <p className="text-sm text-[var(--m-text-secondary)] mb-1">No Support Tickets</p>
            <p className="text-xs text-[var(--m-text-muted)] mb-4">Need help? Create a support ticket.</p>
            <Button size="sm" className="btn-volt" onClick={() => setShowNew(true)} data-testid="support-empty-create-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Create Ticket
            </Button>
          </div>
        ) : (
          tickets.map((t) => (
            <div key={t.id} className="rounded-lg border border-[var(--m-border)] bg-[var(--m-bg-card)] p-3 space-y-1.5" data-testid={`support-ticket-${t.id}`}>
              <div className="flex items-center gap-2">
                {statusIcon(t.status)}
                <span className="text-xs font-mono text-[var(--m-text-muted)]">{t.ticket_number}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full capitalize ${statusColor(t.status)}`}>{(t.status || '').replace(/_/g, ' ')}</span>
              </div>
              <p className="text-sm text-[var(--m-text)] font-medium truncate">{t.subject}</p>
              <p className="text-[10px] text-[var(--m-text-muted)]">{t.created_at ? formatDistanceToNow(new Date(t.created_at), { addSuffix: true }) : ''}</p>
            </div>
          ))
        )}
      </div>
    </MobileLayout>
  );
};

/* ════════════════════════════════════════════════════════════
   Main Settings Hub
   ════════════════════════════════════════════════════════════ */
const MobileBusinessSettings = () => {
  const { api, operator, hasFeature, can, isAdmin } = useMobileAuth();
  const [active, setActive] = useState(null);

  if (active === 'organization') return <OrganisationScreen api={api} operator={operator} onBack={() => setActive(null)} />;
  if (active === 'users') return <UsersScreen api={api} onBack={() => setActive(null)} />;
  if (active === 'billing') return <BillingScreen api={api} onBack={() => setActive(null)} />;
  if (active === 'accounting') return <AccountingScreen api={api} onBack={() => setActive(null)} />;
  if (active === 'timesheets') return <TimesheetsScreen api={api} operator={operator} onBack={() => setActive(null)} />;
  if (active === 'rules') return <RulesScreen api={api} operator={operator} onBack={() => setActive(null)} />;
  if (active === 'notifications') return <NotificationsScreen api={api} onBack={() => setActive(null)} />;
  if (active === 'support') return <SupportScreen api={api} onBack={() => setActive(null)} />;

  // Permission-aware settings items — accounts users see billing/accounting; admin sees all
  const SETTINGS_ITEMS = [
    { key: 'organization', icon: Building2, label: 'Organisation', desc: 'Company name, logo, address & timezone', testId: 'settings-org', show: isAdmin() },
    { key: 'users', icon: Users, label: 'Users', desc: 'Invite & manage office users', testId: 'settings-users', show: isAdmin() },
    { key: 'billing', icon: CreditCard, label: 'Billing', desc: 'Subscription, plan & payment details', testId: 'settings-billing', show: isAdmin() || can('billing.read') },
    { key: 'accounting', icon: Link2, label: 'Accounting', desc: 'Xero, MYOB & QuickBooks connections', testId: 'settings-accounting', show: isAdmin() || can('accounting.read') },
    { key: 'timesheets', icon: Clock, label: 'Timesheets', desc: 'Pay periods, overtime & timesheet rules', testId: 'settings-timesheets', show: isAdmin() && hasFeature('timesheets') },
    { key: 'rules', icon: ScrollText, label: 'Rules', desc: 'Docket & invoice rules configuration', testId: 'settings-rules', show: isAdmin() },
    { key: 'notifications', icon: Bell, label: 'Notifications', desc: 'Business notification preferences', testId: 'settings-notifications', show: isAdmin() },
    { key: 'support', icon: LifeBuoy, label: 'Support', desc: 'Help, guides & contact support', testId: 'settings-support', show: true },
  ].filter(s => s.show);

  return (
    <MobileLayout>
      <ManageHeader title="Settings" subtitle="Business configuration" backTo="/mobile/manage" />
      <div className="p-4 space-y-2" data-testid="settings-hub">
        {SETTINGS_ITEMS.map((s) => {
          const Icon = s.icon;
          return (
            <button key={s.key} onClick={() => setActive(s.key)}
              className="w-full flex items-center gap-4 px-4 py-3.5 rounded-lg border border-[var(--m-border)] bg-[var(--m-bg-card)] hover:bg-[var(--m-bg-elevated)] active:bg-[var(--m-bg-elevated)] transition-colors"
              data-testid={s.testId}>
              <div className="w-9 h-9 rounded-lg bg-[var(--m-bg)] flex items-center justify-center shrink-0">
                <Icon className="h-5 w-5 text-[#ffd108]" />
              </div>
              <div className="flex-1 text-left">
                <div className="text-sm text-[var(--m-text)] font-medium">{s.label}</div>
                <div className="text-xs text-[var(--m-text-secondary)]">{s.desc}</div>
              </div>
              <ChevronRight className="h-4 w-4 text-[var(--m-text-muted)]" />
            </button>
          );
        })}
      </div>
    </MobileLayout>
  );
};

export default MobileBusinessSettings;
